import { useState, type FormEvent } from 'react';

export default function Footer() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const code = parsed?.code;
      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && code === 'OK') {
        setFormStatus('success');
        setFormError('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(serverMsg || 'Something went wrong. Please try again.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Network error. Please check your connection and try again.');
    }
  };

  return (
    <footer className="bg-foreground-950 text-white mx-4 md:mx-8 mb-6 rounded-3xl overflow-hidden">
      <div className="px-6 md:px-12 py-14 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1.5fr] gap-10 md:gap-8">
          <div>
            <h3 className="font-heading text-2xl md:text-3xl font-semibold mb-4">Stay Updated</h3>
            <p className="font-body text-white/50 text-sm mb-6 leading-relaxed max-w-sm">
              Get the latest CSSBuy spreadsheet finds, trending categories, and exclusive deals delivered straight to your inbox.
            </p>

            {formStatus === 'success' && (
              <p className="font-body text-sm text-primary-400 mb-4">Thanks for subscribing! Check your inbox soon.</p>
            )}
            {formStatus === 'error' && (
              <p className="font-body text-sm text-accent-400 mb-4">{formError}</p>
            )}

            <form
              id="newsletter-form"
              data-readdy-form
              action="https://readdy.ai/api/form/d95q9djjjt5fme3c51ag"
              method="POST"
              onSubmit={handleSubmit}
              className="flex flex-col sm:flex-row gap-3"
            >
              <input
                type="text"
                name="phone_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <input
                type="email"
                name="email"
                placeholder="Your email address"
                required
                className="flex-1 bg-transparent border-0 border-b border-white/20 pb-2.5 text-white text-sm font-body placeholder:text-white/30 focus:outline-none focus:border-white/50 transition-colors"
              />
              <button
                type="submit"
                className="px-6 py-2.5 bg-white text-foreground-950 font-label font-semibold text-sm rounded-full hover:bg-white/90 transition-all duration-200 whitespace-nowrap cursor-pointer"
              >
                Subscribe &rarr;
              </button>
            </form>
          </div>

          <div>
            <h4 className="font-label font-semibold text-sm text-white/80 mb-4">Quick Links</h4>
            <ul className="space-y-3">
              {['CSSBuy Spreadsheet', 'Kakobuy Spreadsheet', 'Superbuy Spreadsheet', 'Oopbuy Spreadsheet'].map((link) => (
                <li key={link}>
                  <a
                    href="https://kaspreadsheet.com"
                    target="_blank"
                    rel="noopener nofollow"
                    className="font-body text-white/45 text-sm hover:text-white/70 transition-colors duration-200 underline decoration-white/10 underline-offset-4 cursor-pointer"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-label font-semibold text-sm text-white/80 mb-4">Contact</h4>
            <p className="font-body text-white/45 text-sm mb-1">hello@cssspreadsheet.space</p>
            <p className="font-body text-white/45 text-sm mb-6">support@cssspreadsheet.space</p>
            <h4 className="font-label font-semibold text-sm text-white/80 mb-3">Address</h4>
            <p className="font-body text-white/35 text-sm leading-relaxed">
              221B Baker Street
              <br />
              London, NW1 6XE
              <br />
              United Kingdom
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/8 px-6 md:px-12 py-5">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            {['ri-twitter-x-line', 'ri-instagram-line', 'ri-reddit-line'].map((icon) => (
              <a
                key={icon}
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="text-white/30 hover:text-white/60 transition-colors duration-200 cursor-pointer"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className={icon}></i>
                </span>
              </a>
            ))}
          </div>
          <p className="font-body text-white/25 text-xs text-center">
            &copy; {new Date().getFullYear()} CSS Spreadsheet. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <a href="#" className="font-body text-white/25 text-xs underline decoration-white/10 underline-offset-4 hover:text-white/50 transition-colors duration-200 cursor-pointer">
              Privacy Policy
            </a>
            <a href="#" className="font-body text-white/25 text-xs underline decoration-white/10 underline-offset-4 hover:text-white/50 transition-colors duration-200 cursor-pointer">
              Terms
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}